import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Button,
  Typography,
  Box,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { supabase } from '../backend/supabaseClient';
import {
  sendCommand,
  COMMANDS,
  parseBluetoothData,
  MESSAGE_TYPES
} from '../bluetooth/bluetoothUtils';
import './Calibration.css';

const CALIBRATION_STAGES = {
  IDLE: 'idle',
  INSTRUCTIONS: 'instructions',
  FLAT_GAIN: 'flatGain',
  FLAT_IMU: 'flatImu',
  WAIT_POINTE: 'waitPointe',
  POINTE_GAIN: 'pointeGain',
  POINTE_IMU: 'pointeImu',
  COMPLETE: 'complete',
  ERROR: 'error',
  CANCELLED: 'cancelled'
};

const CALIBRATION_STEPS = [
  'Stand Flat',
  'Flat IMU',
  'Rise to Pointe',
  'Pointe Calibration',
  'Complete'
];

const CalibrationModal = ({
  open,
  onClose,
  characteristic,
  commandCharacteristic,
  deviceId,
  foot,
  onCalibrationComplete
}) => {
  const [stage, setStage] = useState(CALIBRATION_STAGES.INSTRUCTIONS);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [gainProgress, setGainProgress] = useState(0);
  const [imuProgress, setImuProgress] = useState(0);
  const [calibrationData, setCalibrationData] = useState({
    flatGain: null,
    flatMaxReading: null,
    flatAz: null,
    flatAy: null,
    pointeGain: null,
    pointeMaxReading: null,
    pointeAz: null,
    pointeAy: null,
    thresholdZ: null,
    thresholdY: null,
    finalGain: null
  });

  const notificationHandlerRef = useRef(null);
  const timeoutRef = useRef(null);
  const imuIntervalRef = useRef(null);
  const calibrationDataRef = useRef(calibrationData);

  useEffect(() => {
    calibrationDataRef.current = calibrationData;
  }, [calibrationData]);

  const startImuProgressTimer = useCallback(() => {
    setImuProgress(0);

    if (imuIntervalRef.current) {
      clearInterval(imuIntervalRef.current);
    }

    let progress = 0;
    imuIntervalRef.current = setInterval(() => {
      progress += 2;
      setImuProgress(progress);
      if (progress >= 100) {
        clearInterval(imuIntervalRef.current);
        imuIntervalRef.current = null;
      }
    }, 100);
  }, []);

  const saveCalibrationData = useCallback(async (finalGain, thresholdZ, thresholdY) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.warn('No user logged in, skipping calibration save');
        return;
      }

      const currentCalData = calibrationDataRef.current;

      const calibrationRecord = {
        user_id: user.id,
        device_id: deviceId,
        foot: foot,
        timestamp: new Date().toISOString(),
        flat_gain: currentCalData.flatGain,
        flat_max_reading: currentCalData.flatMaxReading,
        pointe_gain: currentCalData.pointeGain,
        pointe_max_reading: currentCalData.pointeMaxReading,
        final_gain: finalGain,
        flat_az: currentCalData.flatAz,
        flat_ay: currentCalData.flatAy,
        pointe_az: currentCalData.pointeAz,
        pointe_ay: currentCalData.pointeAy,
        threshold_z: thresholdZ,
        threshold_y: thresholdY,
        calibration_successful: true
      };

      const { data, error: saveError } = await supabase
        .from('calibrations')
        .insert([calibrationRecord])
        .select();

      if (saveError) {
        console.error('Error saving calibration:', saveError);
        return;
      }

      console.log('✅ Calibration saved:', data);

      if (onCalibrationComplete) {
        onCalibrationComplete({
          ...currentCalData,
          finalGain,
          thresholdZ,
          thresholdY,
          calibrationId: data[0]?.id
        });
      }

    } catch (err) {
      console.error('Error saving calibration to Supabase:', err);
    }
  }, [deviceId, foot, onCalibrationComplete]);

  const handleCalibrationMessage = useCallback((parsed) => {
    console.log('🔧 Calibration message:', parsed);

    const { event, state, instruction, gain, thresholdZ, thresholdY, phase, reading, az, ay } = parsed;

    if (event === 'calibration') {
      console.log('🔄 Calibration state change:', state, instruction);

      switch (state) {
        case 'start':
          setStage(CALIBRATION_STAGES.FLAT_GAIN);
          setActiveStep(0);
          setGainProgress(0);
          setStatusMessage(instruction || 'Stand flat on both feet');
          break;

        case 'flatImu':
          setStage(CALIBRATION_STAGES.FLAT_IMU);
          setActiveStep(1);
          setImuProgress(0);
          setStatusMessage(instruction || 'Hold still...');
          startImuProgressTimer();
          break;

        case 'waitPointe':
          setStage(CALIBRATION_STAGES.WAIT_POINTE);
          setActiveStep(2);
          setStatusMessage(instruction || 'Now rise to pointe position');
          break;

        case 'pointeGain':
          setStage(CALIBRATION_STAGES.POINTE_GAIN);
          setActiveStep(3);
          setGainProgress(0);
          setStatusMessage(instruction || 'Calibrating pointe pressure...');
          break;

        case 'pointeImu':
          setStage(CALIBRATION_STAGES.POINTE_IMU);
          setActiveStep(3);
          setImuProgress(0);
          setStatusMessage(instruction || 'Hold still on pointe...');
          startImuProgressTimer();
          break;

        case 'complete':
          setStage(CALIBRATION_STAGES.COMPLETE);
          setActiveStep(4);
          setCalibrationData(prev => ({
            ...prev,
            finalGain: gain,
            thresholdZ,
            thresholdY
          }));
          setStatusMessage('Calibration complete!');
          setTimeout(() => {
            saveCalibrationData(gain, thresholdZ, thresholdY);
          }, 100);
          break;

        case 'cancelled':
          setStage(CALIBRATION_STAGES.CANCELLED);
          setStatusMessage('Calibration was cancelled');
          break;

        default:
          console.log('⚠️ Unknown calibration state:', state);
          break;
      }
      return;
    }

    switch (event) {
      case 'gainAdjust':
        console.log('📈 Gain adjusting:', parsed);
        setGainProgress(prev => Math.min(prev + 10, 90));

        if (phase === 'flat') {
          setCalibrationData(prev => ({
            ...prev,
            flatGain: gain,
            flatMaxReading: reading
          }));
          setStatusMessage(`Adjusting flat gain: ${gain} (reading: ${reading})`);
        } else {
          setCalibrationData(prev => ({
            ...prev,
            pointeGain: gain,
            pointeMaxReading: reading
          }));
          setStatusMessage(`Adjusting pointe gain: ${gain} (reading: ${reading})`);
        }
        break;

      case 'gainDone':
        console.log('✅ Gain done:', parsed);
        setGainProgress(100);

        if (phase === 'flat') {
          setCalibrationData(prev => ({
            ...prev,
            flatGain: gain,
            flatMaxReading: reading
          }));
          setStatusMessage(`Flat gain complete: ${gain}`);
        } else {
          setCalibrationData(prev => ({
            ...prev,
            pointeGain: gain,
            pointeMaxReading: reading
          }));
          setStatusMessage(`Pointe gain complete: ${gain}`);
        }
        break;

      case 'imuCalDone':
        console.log('✅ IMU cal done:', parsed);
        setImuProgress(100);

        if (phase === 'flat') {
          setCalibrationData(prev => ({
            ...prev,
            flatAz: az,
            flatAy: ay
          }));
          setStatusMessage(`Flat IMU done: Z=${az?.toFixed(2)}, Y=${ay?.toFixed(2)}`);
        } else {
          setCalibrationData(prev => ({
            ...prev,
            pointeAz: az,
            pointeAy: ay
          }));
          setStatusMessage(`Pointe IMU done: Z=${az?.toFixed(2)}, Y=${ay?.toFixed(2)}`);
        }
        break;

      case 'gainReset':
        setStatusMessage(`Gain reset to ${gain}`);
        break;

      case 'error':
        console.error('❌ Calibration error:', parsed.error);
        setError(parsed.error);
        setStage(CALIBRATION_STAGES.ERROR);
        break;

      case 'imuInit':
        if (!parsed.success) {
          console.error('❌ IMU init failed');
          setError('IMU initialization failed');
          setStage(CALIBRATION_STAGES.ERROR);
        }
        break;

      case 'connected':
      case 'deviceReady':
      case 'pong':
        break;

      default:
        console.log('⚠️ Unhandled event:', event, parsed);
        if (parsed.raw) {
          setStatusMessage(parsed.raw);
        }
        break;
    }
  }, [startImuProgressTimer, saveCalibrationData]);

  useEffect(() => {
    if (!open || !characteristic) {
      return;
    }

    console.log('📡 Setting up calibration notification listener');

    const handler = (event) => {
      const dataView = event.target.value;
      const parsed = parseBluetoothData(dataView);

      if (!parsed) {
        console.warn('⚠️ Failed to parse calibration data');
        return;
      }

      if (parsed.type === MESSAGE_TYPES.TEXT_MESSAGE) {
        handleCalibrationMessage(parsed);
      } else if (parsed.type === MESSAGE_TYPES.DATA_PACKET) {
        console.log('📊 Data packet during calibration:', {
          maxSensor: Math.max(...parsed.sensors),
          onPointe: parsed.onPointe
        });
      }
    };

    characteristic.addEventListener('characteristicvaluechanged', handler);
    notificationHandlerRef.current = handler;

    return () => {
      console.log('📡 Removing calibration notification listener');
      if (notificationHandlerRef.current) {
        characteristic.removeEventListener('characteristicvaluechanged', notificationHandlerRef.current);
      }
    };
  }, [open, characteristic, handleCalibrationMessage]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (imuIntervalRef.current) {
        clearInterval(imuIntervalRef.current);
      }
    };
  }, []);

  const startCalibration = async () => {
    try {
      console.log('📡 START_CALIBRATION:', COMMANDS.START_CALIBRATION);

      setError(null);
      setStage(CALIBRATION_STAGES.FLAT_GAIN);
      setActiveStep(0);
      setGainProgress(0);
      setImuProgress(0);
      setCalibrationData({
        flatGain: null,
        flatMaxReading: null,
        flatAz: null,
        flatAy: null,
        pointeGain: null,
        pointeMaxReading: null,
        pointeAz: null,
        pointeAy: null,
        thresholdZ: null,
        thresholdY: null,
        finalGain: null
      });

      console.log('📤 About to call sendCommand...');
      const result = await sendCommand(commandCharacteristic, COMMANDS.START_CALIBRATION);
      console.log('✅ sendCommand returned:', result);
      setStatusMessage('Starting calibration...');

    } catch (err) {
      console.error('❌ Failed to start calibration:', err);
      setError(err.message);
      setStage(CALIBRATION_STAGES.ERROR);
    }
  };

  const continueToPointe = async () => {
    try {
      setStatusMessage('Continuing to pointe calibration...');
      await sendCommand(commandCharacteristic, COMMANDS.CONTINUE_POINTE);
    } catch (err) {
      console.error('Failed to continue to pointe:', err);
      setError(err.message);
      setStage(CALIBRATION_STAGES.ERROR);
    }
  };

  const cancelCalibration = async () => {
    try {
      await sendCommand(commandCharacteristic, COMMANDS.CANCEL_CALIBRATION);
      setStage(CALIBRATION_STAGES.CANCELLED);
    } catch (err) {
      console.error('Failed to cancel calibration:', err);
    }
    handleClose();
  };

  const handleClose = () => {
    if ([
      CALIBRATION_STAGES.FLAT_GAIN,
      CALIBRATION_STAGES.FLAT_IMU,
      CALIBRATION_STAGES.POINTE_GAIN,
      CALIBRATION_STAGES.POINTE_IMU
    ].includes(stage)) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (imuIntervalRef.current) {
      clearInterval(imuIntervalRef.current);
    }

    setStage(CALIBRATION_STAGES.INSTRUCTIONS);
    setActiveStep(0);
    setGainProgress(0);
    setImuProgress(0);
    setError(null);
    setStatusMessage('');

    onClose();
  };

  const isCalibrating = [
    CALIBRATION_STAGES.FLAT_GAIN,
    CALIBRATION_STAGES.FLAT_IMU,
    CALIBRATION_STAGES.POINTE_GAIN,
    CALIBRATION_STAGES.POINTE_IMU
  ].includes(stage);

  const renderContent = () => {
    switch (stage) {
      case CALIBRATION_STAGES.INSTRUCTIONS:
        return (
          <Box className="calibration-phase-content">
            <Typography variant="h6" gutterBottom>
              Calibration Instructions - {foot.charAt(0).toUpperCase() + foot.slice(1)} Foot
            </Typography>

            <Alert severity="info" className="calibration-alert">
              This calibration process will optimize your sensor for accurate readings.
            </Alert>

            <Typography variant="body1" paragraph>
              The calibration has two phases:
            </Typography>

            <Box className="calibration-phases-list">
              <Typography variant="subtitle2" className="phase-title phase-flat" gutterBottom>
                Phase 1: Flat Position
              </Typography>
              <Typography variant="body2" paragraph>
                Stand normally with your weight evenly distributed.
              </Typography>

              <Typography variant="subtitle2" className="phase-title phase-pointe" gutterBottom>
                Phase 2: Pointe Position
              </Typography>
              <Typography variant="body2" paragraph>
                Rise to pointe and hold steady.
              </Typography>
            </Box>

            <Box className="calibration-button-group">
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="contained" onClick={startCalibration} size="large">
                Start Calibration
              </Button>
            </Box>
          </Box>
        );

      case CALIBRATION_STAGES.FLAT_GAIN:
      case CALIBRATION_STAGES.FLAT_IMU:
        return (
          <Box className="calibration-phase-content">
            <Typography variant="h6" gutterBottom align="center">
              Phase 1: Flat Position
            </Typography>

            <Stepper activeStep={activeStep} className="calibration-stepper">
              {CALIBRATION_STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box className="calibration-spinner-container">
              <CircularProgress size={80} className="calibration-spinner" />
            </Box>

            <Typography variant="body1" align="center" paragraph>
              {stage === CALIBRATION_STAGES.FLAT_GAIN
                ? 'Stand flat with weight evenly distributed...'
                : 'Hold still - calibrating IMU...'}
            </Typography>

            <Box className="calibration-progress-container">
              <Typography variant="body2" gutterBottom>
                {stage === CALIBRATION_STAGES.FLAT_GAIN ? 'Gain' : 'IMU'} Calibration Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={stage === CALIBRATION_STAGES.FLAT_GAIN ? gainProgress : imuProgress}
                className="calibration-progress-bar"
              />
            </Box>

            <Typography variant="caption" display="block" align="center" className="calibration-status">
              {statusMessage}
            </Typography>
          </Box>
        );

      case CALIBRATION_STAGES.WAIT_POINTE:
        return (
          <Box className="calibration-phase-content calibration-center">
            <Typography variant="h6" gutterBottom>
              Phase 1 Complete!
            </Typography>

            <Stepper activeStep={activeStep} className="calibration-stepper">
              {CALIBRATION_STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <CheckCircleIcon className="calibration-icon calibration-icon-success" />

            <Alert severity="warning" className="calibration-alert">
              <Typography variant="body2">
                <strong>Now rise to pointe position</strong> and hold steady before clicking Continue.
              </Typography>
            </Alert>

            <Box className="calibration-button-group">
              <Button variant="outlined" onClick={cancelCalibration}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={continueToPointe}
                className="calibration-button-secondary"
                size="large"
              >
                Continue to Pointe
              </Button>
            </Box>
          </Box>
        );

      case CALIBRATION_STAGES.POINTE_GAIN:
      case CALIBRATION_STAGES.POINTE_IMU:
        return (
          <Box className="calibration-phase-content">
            <Typography variant="h6" gutterBottom align="center">
              Phase 2: Pointe Position
            </Typography>

            <Stepper activeStep={activeStep} className="calibration-stepper">
              {CALIBRATION_STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box className="calibration-spinner-container">
              <CircularProgress size={80} className="calibration-spinner calibration-spinner-secondary" />
            </Box>

            <Typography variant="body1" align="center" paragraph>
              {stage === CALIBRATION_STAGES.POINTE_GAIN
                ? 'Hold pointe position - calibrating pressure...'
                : 'Hold still on pointe - calibrating IMU...'}
            </Typography>

            <Box className="calibration-progress-container">
              <Typography variant="body2" gutterBottom>
                {stage === CALIBRATION_STAGES.POINTE_GAIN ? 'Gain' : 'IMU'} Calibration Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={stage === CALIBRATION_STAGES.POINTE_GAIN ? gainProgress : imuProgress}
                className="calibration-progress-bar calibration-progress-secondary"
              />
            </Box>

            <Typography variant="caption" display="block" align="center" className="calibration-status">
              {statusMessage}
            </Typography>
          </Box>
        );

      case CALIBRATION_STAGES.COMPLETE:
        return (
          <Box className="calibration-phase-content calibration-center">
            <CheckCircleIcon className="calibration-icon calibration-icon-success calibration-icon-large" />

            <Typography variant="h5" gutterBottom>
              Calibration Complete!
            </Typography>

            <Box className="calibration-success-box">
              <Typography variant="body1">
                Final Gain: <strong>{calibrationData.finalGain}</strong>
              </Typography>
              <Typography variant="body1">
                Threshold Z: <strong>{calibrationData.thresholdZ?.toFixed(2)}</strong>
              </Typography>
              <Typography variant="body1">
                Threshold Y: <strong>{calibrationData.thresholdY?.toFixed(2)}</strong>
              </Typography>
            </Box>

            <Button variant="contained" onClick={handleClose} size="large">
              Done
            </Button>
          </Box>
        );

      case CALIBRATION_STAGES.ERROR:
        return (
          <Box className="calibration-phase-content calibration-center">
            <ErrorIcon className="calibration-icon calibration-icon-error calibration-icon-large" />

            <Typography variant="h5" gutterBottom>
              Calibration Failed
            </Typography>

            <Alert severity="error" className="calibration-alert">
              {error || 'An unknown error occurred during calibration.'}
            </Alert>

            <Box className="calibration-button-group">
              <Button variant="outlined" onClick={handleClose}>
                Close
              </Button>
              <Button variant="contained" onClick={startCalibration}>
                Try Again
              </Button>
            </Box>
          </Box>
        );

      case CALIBRATION_STAGES.CANCELLED:
        return (
          <Box className="calibration-phase-content calibration-center">
            <WarningIcon className="calibration-icon calibration-icon-warning" />

            <Typography variant="h6" gutterBottom>
              Calibration Cancelled
            </Typography>

            <Button variant="contained" onClick={handleClose}>
              Close
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isCalibrating ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isCalibrating}
      className={`calibration-dialog ${foot === 'right' ? 'right-foot' : 'left-foot'}`}
    >
      <DialogTitle>
        🩰 Sensor Calibration - {foot.charAt(0).toUpperCase() + foot.slice(1)} Foot
      </DialogTitle>
      <DialogContent>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}

export default CalibrationModal;
